import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../cn.js';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Renders a spinner and blocks interaction. */
  loading?: boolean;
}

/**
 * Variants are named by INTENT, not appearance — `primary`/`danger`, never `blue`/`red`. When the
 * real design lands and the accent changes, a component asking for "the primary action" still
 * means the right thing; one asking for "the blue button" does not.
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-fg-on-accent hover:bg-accent-hover',
  secondary: 'bg-surface text-fg border border-border hover:bg-bg-subtle',
  ghost: 'bg-transparent text-fg-muted hover:bg-bg-subtle hover:text-fg',
  danger: 'bg-danger text-fg-on-accent hover:opacity-90',
};

const SIZES: Record<Size, string> = {
  sm: 'h-10 px-2.5 text-xs gap-1.5 sm:h-7',
  md: 'h-[var(--control-height)] px-3 gap-2',
  lg: 'h-11 px-4 text-md gap-2 sm:h-10',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // `loading` must also disable: a form that looks busy but still accepts clicks is how
      // duplicate submissions happen, and on a payout endpoint that is a real incident. The API's
      // Idempotency-Key is the backstop; not letting it happen is the fix.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-colors duration-[var(--duration-fast)]',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading && (
        <span
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  );
});
